/**
 * Per-screen namespaced catalogs (PRD §6). Each namespace is a flat key→string map owned by
 * whichever PRD owns that surface; P6 owns `common`, `drawer`, `nudge`, `installPrompt`,
 * `auth`, `access` (the DenyReason label layer, §3), `about` (the drawer's About screen — a shell
 * surface, same owner as the row that opens it), and `preferences` (D188's appearance screen, a
 * shell surface for the same reason `about` is). **The eight `editName*` keys moved from `drawer`
 * to `preferences` with the control they belong to (D188)**: the sidebar's pencil is gone, so
 * leaving a screen's copy in the sidebar's namespace would have stranded it where nothing renders
 * it. **The `update` namespace was retired by D155**:
 * its Update row applies the waiting shell on the tap, so the sheet that owned that copy is gone
 * and the row's two strings live in `drawer` with the rest of the sidebar. A later
 * PRD adds its own namespace
 * file rather than editing one of these — `home`/`gate`/`personalize`/`people`/`invite`/
 * `install`/`removeMe`/`contacts` are P7's — `contacts` being the one shared by two of
 * them (the invite flow and the install flow's owner step both open the OS contact picker), which
 * is why its copy sits in a namespace of its own rather than in either screen's (implementation-notes.md → P7).
 * **`location` is P17's**, for the same reason `contacts` is its own: the picker is one control
 * that D51 places on three surfaces (install, the later manager edit, and Stage 2's dashboard), so
 * its copy cannot belong to whichever screen happens to mount it first.
 * **`account` is P19's** — the sidebar's account-deletion row and its confirmation (P19 §3). It is
 * a namespace of its own rather than more keys in `drawer` for the same reason `devices` is: the
 * row is P6's sidebar, the surface behind it is another PRD's, and the copy follows the surface.
 * **`androidBeta` is P19's too (D282)** — the launch-time prompt that offers an Android resident
 * the move to the store-delivered app. It is App.tsx that mounts it, the way the sidebar mounts
 * `account`'s row, but the surface it points at is store distribution's; the copy follows the
 * surface. **Its `{{store}}` placeholder is not decoration**: `catalogs.test.ts` refuses the string
 * *Google* in any catalog (D259), and the mark reaches the screen as a literal from `App.tsx` —
 * which is the same mechanism, and the right one, since Google's guidelines say it must not be
 * translated.
 * **`blocked` is P6's (R9 §4.4, D330)** — the one sentence a binary below the build floor renders,
 * on a surface `main.tsx` mounts in place of the app. It is one key and it stays one: the button
 * beneath it reads `drawer`'s own `update`/`updateRestart`/`updateInStore`/`updating`, because the
 * action is D266's rail rather than a control this screen owns, and a copy of those four here
 * would be four strings in five catalogs that agree until one is retranslated.
 */
import enAbout from './catalogs/en/about.json'
import enAccess from './catalogs/en/access.json'
import enAccount from './catalogs/en/account.json'
import enActivity from './catalogs/en/activity.json'
import enAndroidBeta from './catalogs/en/android-beta.json'
import enAuth from './catalogs/en/auth.json'
import enBle from './catalogs/en/ble.json'
import enBlocked from './catalogs/en/blocked.json'
import enCommon from './catalogs/en/common.json'
import enContacts from './catalogs/en/contacts.json'
import enCopyMembers from './catalogs/en/copyMembers.json'
import enDevices from './catalogs/en/devices.json'
import enDrawer from './catalogs/en/drawer.json'
import enGate from './catalogs/en/gate.json'
import enHome from './catalogs/en/home.json'
import enInstall from './catalogs/en/install.json'
import enInstallPrompt from './catalogs/en/install-prompt.json'
import enInvite from './catalogs/en/invite.json'
import enLocation from './catalogs/en/location.json'
import enNudge from './catalogs/en/nudge.json'
import enPeople from './catalogs/en/people.json'
import enPersonalize from './catalogs/en/personalize.json'
import enPreferences from './catalogs/en/preferences.json'
import enRemoveMe from './catalogs/en/removeMe.json'
import enShabbat from './catalogs/en/shabbat.json'
import heAbout from './catalogs/he/about.json'
import heAccess from './catalogs/he/access.json'
import heAccount from './catalogs/he/account.json'
import heActivity from './catalogs/he/activity.json'
import heAndroidBeta from './catalogs/he/android-beta.json'
import heAuth from './catalogs/he/auth.json'
import heBle from './catalogs/he/ble.json'
import heBlocked from './catalogs/he/blocked.json'
import heCommon from './catalogs/he/common.json'
import heContacts from './catalogs/he/contacts.json'
import heCopyMembers from './catalogs/he/copyMembers.json'
import heDevices from './catalogs/he/devices.json'
import heDrawer from './catalogs/he/drawer.json'
import heGate from './catalogs/he/gate.json'
import heHome from './catalogs/he/home.json'
import heInstall from './catalogs/he/install.json'
import heInstallPrompt from './catalogs/he/install-prompt.json'
import heInvite from './catalogs/he/invite.json'
import heLocation from './catalogs/he/location.json'
import heNudge from './catalogs/he/nudge.json'
import hePeople from './catalogs/he/people.json'
import hePersonalize from './catalogs/he/personalize.json'
import hePreferences from './catalogs/he/preferences.json'
import heRemoveMe from './catalogs/he/removeMe.json'
import heShabbat from './catalogs/he/shabbat.json'
import itAbout from './catalogs/it/about.json'
import itAccess from './catalogs/it/access.json'
import itAccount from './catalogs/it/account.json'
import itActivity from './catalogs/it/activity.json'
import itAndroidBeta from './catalogs/it/android-beta.json'
import itAuth from './catalogs/it/auth.json'
import itBle from './catalogs/it/ble.json'
import itBlocked from './catalogs/it/blocked.json'
import itCommon from './catalogs/it/common.json'
import itContacts from './catalogs/it/contacts.json'
import itCopyMembers from './catalogs/it/copyMembers.json'
import itDevices from './catalogs/it/devices.json'
import itDrawer from './catalogs/it/drawer.json'
import itGate from './catalogs/it/gate.json'
import itHome from './catalogs/it/home.json'
import itInstall from './catalogs/it/install.json'
import itInstallPrompt from './catalogs/it/install-prompt.json'
import itInvite from './catalogs/it/invite.json'
import itLocation from './catalogs/it/location.json'
import itNudge from './catalogs/it/nudge.json'
import itPeople from './catalogs/it/people.json'
import itPersonalize from './catalogs/it/personalize.json'
import itPreferences from './catalogs/it/preferences.json'
import itRemoveMe from './catalogs/it/removeMe.json'
import itShabbat from './catalogs/it/shabbat.json'
import ruAbout from './catalogs/ru/about.json'
import ruAccess from './catalogs/ru/access.json'
import ruAccount from './catalogs/ru/account.json'
import ruActivity from './catalogs/ru/activity.json'
import ruAndroidBeta from './catalogs/ru/android-beta.json'
import ruAuth from './catalogs/ru/auth.json'
import ruBle from './catalogs/ru/ble.json'
import ruBlocked from './catalogs/ru/blocked.json'
import ruCommon from './catalogs/ru/common.json'
import ruContacts from './catalogs/ru/contacts.json'
import ruCopyMembers from './catalogs/ru/copyMembers.json'
import ruDevices from './catalogs/ru/devices.json'
import ruDrawer from './catalogs/ru/drawer.json'
import ruGate from './catalogs/ru/gate.json'
import ruHome from './catalogs/ru/home.json'
import ruInstall from './catalogs/ru/install.json'
import ruInstallPrompt from './catalogs/ru/install-prompt.json'
import ruInvite from './catalogs/ru/invite.json'
import ruLocation from './catalogs/ru/location.json'
import ruNudge from './catalogs/ru/nudge.json'
import ruPeople from './catalogs/ru/people.json'
import ruPersonalize from './catalogs/ru/personalize.json'
import ruPreferences from './catalogs/ru/preferences.json'
import ruRemoveMe from './catalogs/ru/removeMe.json'
import ruShabbat from './catalogs/ru/shabbat.json'
import ukAbout from './catalogs/uk/about.json'
import ukAccess from './catalogs/uk/access.json'
import ukAccount from './catalogs/uk/account.json'
import ukActivity from './catalogs/uk/activity.json'
import ukAndroidBeta from './catalogs/uk/android-beta.json'
import ukAuth from './catalogs/uk/auth.json'
import ukBle from './catalogs/uk/ble.json'
import ukBlocked from './catalogs/uk/blocked.json'
import ukCommon from './catalogs/uk/common.json'
import ukContacts from './catalogs/uk/contacts.json'
import ukCopyMembers from './catalogs/uk/copyMembers.json'
import ukDevices from './catalogs/uk/devices.json'
import ukDrawer from './catalogs/uk/drawer.json'
import ukGate from './catalogs/uk/gate.json'
import ukHome from './catalogs/uk/home.json'
import ukInstall from './catalogs/uk/install.json'
import ukInstallPrompt from './catalogs/uk/install-prompt.json'
import ukInvite from './catalogs/uk/invite.json'
import ukLocation from './catalogs/uk/location.json'
import ukNudge from './catalogs/uk/nudge.json'
import ukPeople from './catalogs/uk/people.json'
import ukPersonalize from './catalogs/uk/personalize.json'
import ukPreferences from './catalogs/uk/preferences.json'
import ukRemoveMe from './catalogs/uk/removeMe.json'
import ukShabbat from './catalogs/uk/shabbat.json'
import type { Locale } from './locales'

export const catalogs = {
  en: {
    common: enCommon,
    drawer: enDrawer,
    preferences: enPreferences,
    nudge: enNudge,
    installPrompt: enInstallPrompt,
    auth: enAuth,
    access: enAccess,
    home: enHome,
    gate: enGate,
    personalize: enPersonalize,
    people: enPeople,
    invite: enInvite,
    contacts: enContacts,
    install: enInstall,
    location: enLocation,
    removeMe: enRemoveMe,
    about: enAbout,
    shabbat: enShabbat,
    activity: enActivity,
    devices: enDevices,
    copyMembers: enCopyMembers,
    account: enAccount,
    androidBeta: enAndroidBeta,
    ble: enBle,
    blocked: enBlocked,
  },
  he: {
    common: heCommon,
    drawer: heDrawer,
    preferences: hePreferences,
    nudge: heNudge,
    installPrompt: heInstallPrompt,
    auth: heAuth,
    access: heAccess,
    home: heHome,
    gate: heGate,
    personalize: hePersonalize,
    people: hePeople,
    invite: heInvite,
    contacts: heContacts,
    install: heInstall,
    location: heLocation,
    removeMe: heRemoveMe,
    about: heAbout,
    shabbat: heShabbat,
    activity: heActivity,
    devices: heDevices,
    copyMembers: heCopyMembers,
    account: heAccount,
    androidBeta: heAndroidBeta,
    ble: heBle,
    blocked: heBlocked,
  },
  ru: {
    common: ruCommon,
    drawer: ruDrawer,
    preferences: ruPreferences,
    nudge: ruNudge,
    installPrompt: ruInstallPrompt,
    auth: ruAuth,
    access: ruAccess,
    home: ruHome,
    gate: ruGate,
    personalize: ruPersonalize,
    people: ruPeople,
    invite: ruInvite,
    contacts: ruContacts,
    install: ruInstall,
    location: ruLocation,
    removeMe: ruRemoveMe,
    about: ruAbout,
    shabbat: ruShabbat,
    activity: ruActivity,
    devices: ruDevices,
    copyMembers: ruCopyMembers,
    account: ruAccount,
    androidBeta: ruAndroidBeta,
    ble: ruBle,
    blocked: ruBlocked,
  },
  it: {
    common: itCommon,
    drawer: itDrawer,
    preferences: itPreferences,
    nudge: itNudge,
    installPrompt: itInstallPrompt,
    auth: itAuth,
    access: itAccess,
    home: itHome,
    gate: itGate,
    personalize: itPersonalize,
    people: itPeople,
    invite: itInvite,
    contacts: itContacts,
    install: itInstall,
    location: itLocation,
    removeMe: itRemoveMe,
    about: itAbout,
    shabbat: itShabbat,
    activity: itActivity,
    devices: itDevices,
    copyMembers: itCopyMembers,
    account: itAccount,
    androidBeta: itAndroidBeta,
    ble: itBle,
    blocked: itBlocked,
  },
  uk: {
    common: ukCommon,
    drawer: ukDrawer,
    preferences: ukPreferences,
    nudge: ukNudge,
    installPrompt: ukInstallPrompt,
    auth: ukAuth,
    access: ukAccess,
    home: ukHome,
    gate: ukGate,
    personalize: ukPersonalize,
    people: ukPeople,
    invite: ukInvite,
    contacts: ukContacts,
    install: ukInstall,
    location: ukLocation,
    removeMe: ukRemoveMe,
    about: ukAbout,
    shabbat: ukShabbat,
    activity: ukActivity,
    devices: ukDevices,
    copyMembers: ukCopyMembers,
    account: ukAccount,
    androidBeta: ukAndroidBeta,
    ble: ukBle,
    blocked: ukBlocked,
  },
} as const satisfies Record<Locale, Record<string, Readonly<Record<string, string>>>>

export type Namespace = keyof (typeof catalogs)['en']
export type Key<N extends Namespace> = keyof (typeof catalogs)['en'][N] & string
